import React, { useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Header from '../../Header'
import Footer from '../../Footer'
import Keyboard, { HIDDEN_KEYBOARD_KEYS_LICENCE_PLATE } from '../../Keyboard'
import { setUIForwardAction, setUIBackwardsAction } from '../../../actions'
import { UI } from '../../../constants/ui'
import { setTrailerLicencePlateAction } from '../../../actions/reservationChange'
import { AppState } from '../../../reducers'
import { CustomCaretProps } from '../../CustomCaret'
import './trailer.less'
import FooterMobile from '../../FooterMobile'
import TrailerLicenceImg from '../../../assets/trailer-licence.png'
import { getMode } from '../../../hooks/mode'
import { MODE } from '../../../constants/mode'
import { FormFieldInput } from '../../FormFieldInput'
import { INPUT_TYPE } from '../../../constants/inputTypes'

const mapDispatchToProps = {
  setUIForward: setUIForwardAction,
  setUIBackwards: setUIBackwardsAction,
  setTrailerLicencePlate: setTrailerLicencePlateAction,
}
type DispatchProps = typeof mapDispatchToProps

const mapStateToProps = (state: AppState) => ({
  trailerLicencePlate: state.reservationChange.trailerLicencePlate,
})

type StateProps = ReturnType<typeof mapStateToProps>
type Props = DispatchProps & StateProps

interface CustomMobileProps {
  clearInputField: (currentInput: any) => void
  trailerLicencePlateInput: React.RefObject<HTMLInputElement>
  onChange: (value: string) => void
  onClick: () => void
  onKeyboardDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
}
type MobileProps = Omit<Props, "setTrailerLicencePlate" | "setUIForward"> & CustomMobileProps

const TrailerMobile: React.FC<MobileProps> = ({ setUIBackwards, trailerLicencePlate, clearInputField, trailerLicencePlateInput, onChange, onClick, onKeyboardDown, onSubmit }) => {
  const { t } = useTranslation()
  return (
    <div className="trailer-mobile">
      <div className="trailer-mobile-wrapper">
        <div className="trailer-title" dangerouslySetInnerHTML={{ __html: t('trailer') }}></div>
        <div className="img-trailer-container">
          <img className="img-trailer" src={TrailerLicenceImg} alt="trailer licence" />
        </div>
        <div className="trailer-info">
          <div className="trailer-input-field">
            <FormFieldInput
              id="trailer-licence-plate"
              type={INPUT_TYPE.LICENCE_PLATE}
              label={t('trailerLicencePlate')}
              sublabel={t('useOnlyLettersAndNumbers')}
              isClearNeed={!!trailerLicencePlate}
              clearInputField={clearInputField}
              autoFocus={!trailerLicencePlate}
              inputRef={trailerLicencePlateInput}
              value={trailerLicencePlate}
              onChange={onChange}
              onClick={onClick}
              onKeyDown={onKeyboardDown}
            />
          </div>
        </div>
        <div className="next-button">
          <button className="main-button-mobile" onClick={onSubmit} disabled={!trailerLicencePlate}>
            {t('next')}
          </button>
        </div>
      </div>
      <div className="footer-mobile-container">
        <FooterMobile showPageCount back={() => setUIBackwards(UI.MORE_THAN_ONE_PASSENGER)} />
      </div>
    </div>
  )
}

interface CustomDesktopProps {
  clearInputField: (currentInput: any) => void
  trailerLicencePlateInput: React.RefObject<HTMLInputElement>
  onChange: (value: string) => void
  activeInput: HTMLInputElement | undefined
  onClick: () => void
  refLicencePlateCaret: React.RefObject<any>
  isKeyboardShown: boolean
  setShowKeyboard: (value: boolean) => void
  mode: MODE
  onKeyboardDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
}
type DesktopProps = Pick<Props, "setUIBackwards" | "trailerLicencePlate"> & CustomDesktopProps

const TrailerDesktop: React.FC<DesktopProps> = ({ setUIBackwards, trailerLicencePlate, clearInputField, trailerLicencePlateInput, onChange, onClick, refLicencePlateCaret, isKeyboardShown, setShowKeyboard, activeInput, mode, onKeyboardDown, onSubmit }) => {
  const { t } = useTranslation()
  const isKioskMode = mode === MODE.KIOSK
  return (
    <>
      <Header showPageCount back={() => setUIBackwards(UI.MORE_THAN_ONE_PASSENGER)} />
      <div className="trailer">
        <div className="trailer-title" dangerouslySetInnerHTML={{ __html: t('trailer') }}></div>
        <div className="trailer-info">
          <div className="trailer-input-field">
            <FormFieldInput
              id="trailer-licence-plate"
              type={INPUT_TYPE.LICENCE_PLATE}
              label={t('trailerLicencePlate')}
              sublabel={t('useOnlyLettersAndNumbers')}
              isClearNeed={!!trailerLicencePlate}
              clearInputField={clearInputField}
              autoFocus
              inputRef={trailerLicencePlateInput}
              value={trailerLicencePlate}
              onChange={onChange}
              onClick={onClick}
              onKeyDown={onKeyboardDown}
              caretRef={refLicencePlateCaret}
            />
          </div>
        </div>
        <div className="next-button">
          <button className="main-button" onClick={onSubmit} disabled={!trailerLicencePlate}>
            {t('next')}
          </button>
        </div>
      </div>
      <div className="keyboard-cont">
        {isKioskMode && isKeyboardShown && (
          <Keyboard
            input={activeInput}
            onChange={onChange}
            onClose={() => setShowKeyboard(false)}
            hiddenKeys={HIDDEN_KEYBOARD_KEYS_LICENCE_PLATE}
          />
        )}
      </div>
      <div className="footer-cont">{(!isKeyboardShown || !isKioskMode) && <Footer />}</div>
    </>
  )
}

const Trailer: React.FC<Props> = ({
  setUIForward,
  setUIBackwards,
  setTrailerLicencePlate,
  trailerLicencePlate,
}: Props) => {
  const [activeInput, setActiveInput] = useState()
  const [isKeyboardShown, setShowKeyboard] = useState(true)
  const trailerLicencePlateInput = useRef(null)
  const refLicencePlateCaret = useRef<CustomCaretProps>(null)
  const mode = getMode()

  const updateCaretPosition = () => {
    refLicencePlateCaret.current && refLicencePlateCaret.current.updateCaretPosition()
  }

  const onChange = (value: string) => {
    setTrailerLicencePlate(value.toUpperCase())
    updateCaretPosition()
  }

  const onClick = () => {
    setShowKeyboard(true)
    updateCaretPosition()
  }

  const clearInputField = (currentInput: any) => {
    setActiveInput(currentInput)
    setTrailerLicencePlate('')
  }

  const onSubmit = () => {
    setUIForward(UI.CARGO_LENGTH)
  }

  const onKeyboardDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13 && trailerLicencePlate) {
      onSubmit()
    }
  }

  useEffect(() => {
    trailerLicencePlateInput.current && setActiveInput(trailerLicencePlateInput.current)
  }, [])

  if (mode === MODE.MOBILE) {
    const trailerMobileProps = { setUIBackwards, trailerLicencePlate, clearInputField, trailerLicencePlateInput, onChange, onClick, onKeyboardDown, onSubmit }
    return <TrailerMobile {...trailerMobileProps} />
  } else {
    const trailerDesktopProps = { setUIBackwards, trailerLicencePlate, clearInputField, trailerLicencePlateInput, onChange, onClick, refLicencePlateCaret, isKeyboardShown, setShowKeyboard, activeInput, mode, onKeyboardDown, onSubmit }
    return <TrailerDesktop {...trailerDesktopProps} />
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Trailer)
